import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { RefreshCw, Save, Sparkles, Calendar, CheckCircle, XCircle, ExternalLink, PlayCircle, Bot, ShieldCheck, AlertTriangle } from 'lucide-react';
import { authFetch } from '../../../utils/authFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { v: 'mon', l: 'Mon' }, { v: 'tue', l: 'Tue' }, { v: 'wed', l: 'Wed' },
  { v: 'thu', l: 'Thu' }, { v: 'fri', l: 'Fri' }, { v: 'sat', l: 'Sat' }, { v: 'sun', l: 'Sun' },
];

const AutoBlogPanel = ({ onPostCreated }) => {
  const [settings, setSettings] = useState(null);
  const [topicPool, setTopicPool] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  const load = async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const [s, l] = await Promise.all([
        authFetch(`${API}/api/blog/auto/settings`),
        authFetch(`${API}/api/blog/auto/logs?limit=10`),
      ]);
      if (s.ok) {
        const d = await s.json();
        setSettings(d.settings);
        setTopicPool(d.topic_pool || []);
      } else {
        setLoadErr(`Settings request failed (status ${s.status})`);
      }
      if (l.ok) {
        const d = await l.json();
        setLogs(d.logs || []);
      }
    } catch (e) {
      // authFetch throws "Session expired" after triggering redirect; ignore.
      if (e?.message !== 'Session expired') {
        setLoadErr(e?.message || 'Failed to load Auto-Blog settings');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/api/blog/auto/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          enabled: settings.enabled,
          schedule: settings.schedule,
          auto_publish: settings.auto_publish,
          auto_ping_indexnow: settings.auto_ping_indexnow,
          auto_ping_gsc: settings.auto_ping_gsc,
          topic_keys: settings.topic_keys,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        setSettings(d.settings);
      }
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async (topicKey = null) => {
    setGenerating(true);
    setGenerateResult(null);
    try {
      const res = await authFetch(`${API}/api/blog/auto/generate-now`, {
        method: 'POST',
        body: JSON.stringify(topicKey ? { topic_key: topicKey } : {}),
      });
      const d = await res.json();
      setGenerateResult(d);
      if (res.ok) {
        await load();
        if (onPostCreated) onPostCreated();
      }
    } catch (e) {
      setGenerateResult({ success: false, detail: e.message });
    } finally {
      setGenerating(false);
    }
  };

  const [seeding, setSeeding] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const regenerateDrafts = async () => {
    if (!window.confirm('Delete ALL current draft/blocked blog posts and generate the same number of fresh, brand-safe posts? This runs in the background and can take several minutes (~40s per post).')) return;
    setRegenerating(true);
    try {
      const res = await authFetch(`${API}/api/blog/auto/regenerate-drafts`, { method: 'POST' });
      const d = await res.json();
      if (res.ok) {
        if (d.status === 'noop') {
          window.alert('No draft posts to regenerate — you\'re all clean! ✓');
        } else {
          window.alert(`Deleting ${d.count} old drafts and generating ${d.count} fresh brand-safe posts in the background. This takes a few minutes — refresh the blog list shortly to see them.`);
        }
        await load();
        if (onPostCreated) onPostCreated();
      } else {
        window.alert('Regenerate failed: ' + (d.detail || res.status));
      }
    } catch (e) {
      window.alert('Regenerate failed: ' + e.message);
    } finally {
      setRegenerating(false);
    }
  };

  const auditAll = async () => {
    if (!window.confirm('Scan EVERY blog post for banned brand-accuracy phrases (e.g. "we churn", "our kitchen")? Any matching post will be force-unpublished and flagged so you can review.')) return;
    setAuditing(true);
    setAuditResult(null);
    try {
      const res = await authFetch(`${API}/api/blog/auto/audit-all`, {
        method: 'POST',
      });
      const d = await res.json();
      if (res.ok) {
        setAuditResult(d);
        await load();
        if (onPostCreated) onPostCreated();
      } else {
        window.alert('Audit failed: ' + (d.detail || res.status));
      }
    } catch (e) {
      window.alert('Audit failed: ' + e.message);
    } finally {
      setAuditing(false);
    }
  };

  const seedBackdated = async () => {
    if (!window.confirm('Generate 5 fully-unique, AI-written articles with hero images and back-dated publish dates (so the blog has history)? This takes ~3–4 minutes. Topics are round-robin from the enabled pool.')) return;
    setSeeding(true);
    setGenerateResult(null);
    try {
      const res = await authFetch(`${API}/api/blog/auto/seed-backdated`, {
        method: 'POST',
        body: JSON.stringify({ count: 5, spacing_days: 7, starting_days_ago: 35 }),
      });
      const d = await res.json();
      if (res.ok) {
        const successCount = d.count || 0;
        const errCount = (d.errors || []).length;
        window.alert(`Created ${successCount} backdated posts${errCount ? ` (${errCount} failed — check logs)` : ''}. Refreshing list…`);
        await load();
        if (onPostCreated) onPostCreated();
      } else {
        window.alert('Seeding failed: ' + (d.detail || res.status));
      }
    } catch (e) {
      window.alert('Seeding failed: ' + e.message);
    } finally {
      setSeeding(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 text-center">
        {loadErr ? (
          <div className="space-y-3" data-testid="auto-blog-load-error">
            <div className="inline-flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
              <AlertTriangle className="w-4 h-4" />
              Couldn't load Auto-Blog settings. {loadErr}
            </div>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" onClick={load} className="gap-2">
                <RefreshCw className="w-4 h-4" /> Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.replace('/admin/login?reason=session_expired'); }}
                className="gap-2"
              >
                Log in again
              </Button>
            </div>
          </div>
        ) : (
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-500" />
        )}
      </div>
    );
  }

  const toggleDay = (idx, day) => {
    const sched = [...settings.schedule];
    sched[idx] = { ...sched[idx], day_of_week: day };
    setSettings({ ...settings, schedule: sched });
  };

  const addSlot = () => {
    if (settings.schedule.length >= 7) return;
    setSettings({ ...settings, schedule: [...settings.schedule, { day_of_week: 'wed', hour: 9, minute: 0 }] });
  };

  const removeSlot = (idx) => {
    setSettings({ ...settings, schedule: settings.schedule.filter((_, i) => i !== idx) });
  };

  const toggleTopic = (key) => {
    const current = settings.topic_keys || [];
    setSettings({
      ...settings,
      topic_keys: current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    });
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg p-4 md:p-6 border border-purple-200" data-testid="auto-blog-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-purple-100 rounded-xl"><Bot className="w-6 h-6 text-purple-600" /></div>
        <div className="flex-1">
          <h3 className="text-lg md:text-xl font-bold flex items-center gap-2">
            AI Auto-Posting <Sparkles className="w-4 h-4 text-purple-600" />
          </h3>
          <p className="text-gray-600 text-xs md:text-sm">Claude Sonnet 4.5 writes posts on a schedule, auto-publishes, and pings Google + Bing.</p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-full border-2 border-purple-200">
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            data-testid="auto-blog-enabled-toggle"
          />
          <span className="text-sm font-bold">{settings.enabled ? 'On' : 'Off'}</span>
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Schedule */}
        <div className="bg-white rounded-xl p-4">
          <h4 className="font-bold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-purple-600" /> Schedule (CST)</h4>
          <div className="space-y-2">
            {settings.schedule.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <select
                  value={s.day_of_week}
                  onChange={(e) => toggleDay(i, e.target.value)}
                  className="border-2 rounded px-2 py-1 text-sm"
                  data-testid={`auto-blog-day-${i}`}
                >
                  {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                </select>
                <input
                  type="number" min="0" max="23"
                  value={s.hour}
                  onChange={(e) => { const sched = [...settings.schedule]; sched[i] = { ...s, hour: parseInt(e.target.value || '0') }; setSettings({ ...settings, schedule: sched }); }}
                  className="border-2 rounded px-2 py-1 w-16 text-sm"
                />
                <span className="text-sm">:</span>
                <input
                  type="number" min="0" max="59"
                  value={s.minute}
                  onChange={(e) => { const sched = [...settings.schedule]; sched[i] = { ...s, minute: parseInt(e.target.value || '0') }; setSettings({ ...settings, schedule: sched }); }}
                  className="border-2 rounded px-2 py-1 w-16 text-sm"
                />
                <button onClick={() => removeSlot(i)} className="text-red-500 hover:underline text-xs ml-auto">Remove</button>
              </div>
            ))}
            <button
              onClick={addSlot}
              disabled={settings.schedule.length >= 7}
              className="text-purple-600 hover:underline text-sm font-bold disabled:opacity-40"
            >
              + Add slot
            </button>
          </div>
          <div className="mt-4 space-y-2 border-t pt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings.auto_publish} onChange={(e) => setSettings({ ...settings, auto_publish: e.target.checked })} data-testid="auto-blog-publish-toggle" />
              <span className="text-sm">Auto-publish (vs save as draft)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings.auto_ping_indexnow} onChange={(e) => setSettings({ ...settings, auto_ping_indexnow: e.target.checked })} />
              <span className="text-sm">Ping Bing/IndexNow on publish</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings.auto_ping_gsc} onChange={(e) => setSettings({ ...settings, auto_ping_gsc: e.target.checked })} />
              <span className="text-sm">Resubmit sitemap to Google on publish</span>
            </label>
          </div>
        </div>

        {/* Topics */}
        <div className="bg-white rounded-xl p-4">
          <h4 className="font-bold mb-3">Topic Pool (round-robin)</h4>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {topicPool.map((t) => (
              <label key={t.key} className="flex items-start gap-2 cursor-pointer text-sm" data-testid={`auto-blog-topic-${t.key}`}>
                <input
                  type="checkbox"
                  checked={(settings.topic_keys || []).includes(t.key)}
                  onChange={() => toggleTopic(t.key)}
                  className="mt-1"
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700 gap-2" data-testid="auto-blog-save-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save Settings'}
        </Button>
        <Button onClick={() => generateNow()} disabled={generating} variant="outline" className="border-purple-400 text-purple-700 hover:bg-purple-50 gap-2" data-testid="auto-blog-generate-now-btn">
          {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          {generating ? 'Generating (~30–45s with image)…' : 'Generate Post Now'}
        </Button>
        <Button onClick={seedBackdated} disabled={seeding} variant="outline" className="border-pink-400 text-pink-700 hover:bg-pink-50 gap-2" data-testid="auto-blog-seed-backdated-btn">
          {seeding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {seeding ? `Seeding (~${5*40}s)…` : 'Seed 5 Backdated Articles'}
        </Button>
        <Button onClick={auditAll} disabled={auditing} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-2" data-testid="auto-blog-audit-btn">
          {auditing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {auditing ? 'Auditing…' : 'Audit All Posts'}
        </Button>
        <Button onClick={regenerateDrafts} disabled={regenerating} variant="outline" className="border-rose-400 text-rose-700 hover:bg-rose-50 gap-2" data-testid="auto-blog-regenerate-drafts-btn">
          {regenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {regenerating ? 'Starting…' : 'Regenerate Blocked Drafts'}
        </Button>
      </div>

      {auditResult && (
        <div className={`mt-4 rounded-xl p-3 text-sm ${auditResult.flagged_count > 0 ? 'bg-amber-50 border border-amber-300' : 'bg-green-50 border border-green-200'}`} data-testid="audit-result">
          <div className="flex items-start gap-2">
            <ShieldCheck className={`w-5 h-5 mt-0.5 ${auditResult.flagged_count > 0 ? 'text-amber-600' : 'text-green-600'}`} />
            <div className="flex-1">
              <div className="font-bold">
                Scanned {auditResult.scanned} posts — {auditResult.flagged_count} flagged
                {auditResult.cleared?.length > 0 && `, ${auditResult.cleared.length} cleared`}
              </div>
              {auditResult.flagged?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {auditResult.flagged.map((f) => (
                    <li key={f.id} className="text-xs">
                      <a href={`/blog/${f.slug}`} target="_blank" rel="noopener noreferrer" className="font-bold text-amber-700 hover:underline">
                        {f.title}
                      </a>
                      <span className="ml-2 text-amber-600">→ unpublished</span>
                      <span className="ml-1 text-gray-500">({f.hits.slice(0, 3).map(h => `"${h}"`).join(', ')}{f.hits.length > 3 ? '…' : ''})</span>
                    </li>
                  ))}
                </ul>
              )}
              {auditResult.flagged_count === 0 && <div className="text-xs text-green-700">All posts are brand-accurate. ✓</div>}
            </div>
          </div>
        </div>
      )}

      {generateResult && (
        <div className={`mt-4 rounded-xl p-3 text-sm ${generateResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          {generateResult.success ? (
            <div className="flex items-start gap-2 text-green-800">
              <CheckCircle className="w-5 h-5 mt-0.5" />
              <div>
                <div className="font-bold">{generateResult.title}</div>
                <a href={`/blog/${generateResult.slug}`} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline text-xs flex items-center gap-1 mt-1">
                  <ExternalLink className="w-3 h-3" /> View live post
                </a>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-red-800">
              <XCircle className="w-5 h-5 mt-0.5" />
              <div>
                <div className="font-bold">Generation failed</div>
                <div className="text-xs">{generateResult.detail || JSON.stringify(generateResult)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent runs */}
      {logs.length > 0 && (
        <div className="mt-6 bg-white rounded-xl p-4">
          <h4 className="font-bold mb-3 text-sm">Recent Auto-Generated Posts</h4>
          <ul className="space-y-1 text-sm">
            {logs.map((log) => (
              <li key={log.id} className="flex items-center gap-2 text-gray-700">
                <span className="text-xs text-gray-400 w-32 flex-shrink-0">{new Date(log.created_at).toLocaleString()}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex-shrink-0">{log.topic_key}</span>
                <a href={`/blog/${log.slug}`} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">{log.title}</a>
                {!log.is_published && <span className="text-xs text-amber-600 font-bold">(draft)</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {settings.last_status && settings.last_status !== 'ok' && (
        <div className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-xs">
          Last scheduled run: <strong>{settings.last_status}</strong>
        </div>
      )}
    </div>
  );
};

export default AutoBlogPanel;
